import { Button } from '@ordo/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@ordo/ui/card'
import { Input } from '@ordo/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@ordo/ui/select'
import { Separator } from '@ordo/ui/separator'
import { toast } from '@ordo/ui/sonner'
import { BackLink } from 'compositions/BackLink'
import { Page } from 'compositions/Page'
import { useAddGroupContact, useCreateContactGroup } from 'hooks/api'
import { getApiErrorMessage } from 'hooks/useFetchApi'
import { Save } from 'lucide-react'
import { useCallback, useState } from 'react'
import { useNavigate } from 'react-router'
import type { Ruleset } from 'types/communications'
import type { Contact } from 'types/contacts'

import { RuleBuilder } from './rule-builder'
import { StaticContactPicker } from './static-contact-picker'
export function ListNew() {
  const navigate = useNavigate()
  const { perform: createGroup, loading: saving } = useCreateContactGroup()
  const { perform: addContact } = useAddGroupContact()

  const [name, setName] = useState('')
  const [dynamic, setDynamic] = useState(false)
  const [ruleset, setRuleset] = useState<Ruleset>({ match: 'all', rules: [] })
  const [selectedContacts, setSelectedContacts] = useState<Contact[]>([])

  const handleAddContact = useCallback((contact: Contact) => {
    setSelectedContacts((prev) => (prev.some((c) => c.id === contact.id) ? prev : [...prev, contact]))
  }, [])

  const handleRemoveContact = useCallback((contactId: string) => {
    setSelectedContacts((prev) => prev.filter((c) => c.id !== contactId))
  }, [])

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault()
      if (!name.trim()) {
        return
      }
      try {
        const group = await createGroup({
          name: name.trim(),
          dynamic,
          ruleset: dynamic ? ruleset : undefined,
        })
        if (!dynamic) {
          for (const contact of selectedContacts) {
            await addContact(group.id, contact.id)
          }
        }
        navigate(`/lists/${group.id}`, { replace: true })
      } catch (err) {
        toast.error('Failed to create list', { description: getApiErrorMessage(err) })
      }
    },
    [name, dynamic, ruleset, selectedContacts, createGroup, addContact, navigate],
  )

  return (
    <Page>
      <div className="mb-4">
        <BackLink to="/lists">Back to Lists</BackLink>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>New List</CardTitle>
        </CardHeader>

        <Separator />

        <CardContent className="pt-6">
          <form onSubmit={handleSubmit}>
            <div className="space-y-4">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <label htmlFor="name" className="text-sm font-medium text-muted-foreground">
                    Name
                  </label>
                  <Input id="name" value={name} onChange={(e) => setName(e.target.value)} required />
                </div>
                <div className="space-y-1.5">
                  <label htmlFor="type" className="text-sm font-medium text-muted-foreground">
                    Type
                  </label>
                  <Select value={dynamic ? 'dynamic' : 'static'} onValueChange={(v) => setDynamic(v === 'dynamic')}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="static">Static</SelectItem>
                      <SelectItem value="dynamic">Dynamic</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <Separator />

              {dynamic ? (
                <RuleBuilder ruleset={ruleset} onChange={setRuleset} />
              ) : (
                <StaticContactPicker
                  selectedContacts={selectedContacts}
                  onAdd={handleAddContact}
                  onRemove={handleRemoveContact}
                />
              )}
            </div>

            <div className="mt-6 flex justify-end">
              <Button type="submit" disabled={saving || !name.trim()}>
                <Save className="mr-1 h-4 w-4" />
                {saving ? 'Creating...' : 'Create List'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </Page>
  )
}
